import { useAuth } from "@clerk/clerk-react";
import { Box, Button, Grid, Typography } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SlideAnimationContext } from "contexts/SlideAnimation";
import { SubscriptionContext } from "contexts/Subscription";
import { useContext, useEffect } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { routes } from "schemas/routes";

import { NotInterested } from "features/Home/UpgradeDialog/NotInterested";
import Subscription from "features/Home/UpgradeDialog/Subscription";
import SubscriptionButton from "features/Settings/SubscriptionButton";
import { fetcherAuth } from "utils/api";
import { getSource } from "utils/tracking";
import { useTracking } from "utils/useTracking";

import { vaOnboardingNavigate } from "../../Onboarding/helper";
import styles from "./styles";

const VAPaidPortal = () => {
  const { getToken } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { currentOrg } = useContext(OrganizationUserContext);
  const slideContext = useContext(SlideAnimationContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const { trackVaStep } = useTracking();
  const source = getSource();

  const { setCurrentOrg } = useContext(OrganizationUserContext);

  const successCheckout = searchParams.get("checkout") === "success";

  const { setAlert, setErrorAlert } = useContext(AlertContext);

  const { subscription, isVaPlan } = useContext(SubscriptionContext);
  const isVaUser = subscription?.isSubscribed && isVaPlan;

  const handleBack = async () => {
    if (slideContext) {
      await slideContext.slideOutDown();
      slideContext.slideInDown();
    }
    navigate(
      `/${vaOnboardingNavigate(location.pathname, "back")}${location.search}`,
    );
  };

  const handleNotInterested = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/onboarding/va-not-interested`,
        "POST",
      );
      setCurrentOrg(res.organization);
      navigate(`/${routes.dashboard}${location.search}`, {
        replace: true,
      });
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const handleCloseNotInterestedDialog = () => {
    searchParams.delete("checkout");
    setSearchParams(searchParams);
  };

  const handleNext = () => {
    trackVaStep(location.pathname, source);
    navigate(
      `/${vaOnboardingNavigate(location.pathname, "next")}${location.search}`,
    );
  };

  useEffect(() => {
    if (successCheckout) {
      handleNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successCheckout]);

  return (
    <>
      <Typography variant="h3" gutterBottom sx={{ textAlign: "center", mt: 0 }}>
        📝 Bento's Virtual Assistant Program
      </Typography>

      <Typography
        variant="subtitle1"
        gutterBottom
        sx={{ textAlign: "center", mb: 3 }}
      >
        Save hundred of hours and make more money with Bento's Virtual Assistant
        Program.
      </Typography>

      <Box sx={{ maxWidth: 600, display: "block", margin: "auto" }}>
        <Box sx={styles.line}>
          <Box component="i" className="fa-solid fa-check" sx={styles.icon} />{" "}
          Get a personalized email pitch written for you through our tailored
          onboarding experience.
        </Box>
        <Box sx={styles.line}>
          <Box component="i" className="fa-solid fa-check" sx={styles.icon} />{" "}
          Get hundreds of pitches sent for you every month without lifting a
          finger.
        </Box>
      </Box>

      <Grid container justifyContent="center" sx={{ my: 2 }}>
        <SubscriptionButton />
      </Grid>
      <Grid container justifyContent="center">
        <Box sx={{ maxWidth: "500px" }}>
          <Subscription
            setAlert={setAlert}
            upgradeDialogSource={"Upgrade VA Button"}
          />
        </Box>
      </Grid>

      <Grid item xs="auto" container columnGap={1} sx={{ mt: 4 }}>
        <Grid item xs="auto">
          <Button onClick={handleBack} color="secondary" disableElevation>
            Back
          </Button>
          {!(successCheckout || isVaUser) && (
            <Button
              size="small"
              sx={styles.notInterested}
              onClick={handleNotInterested}
              color="secondary"
            >
              Not Interested
            </Button>
          )}
        </Grid>
        <Grid item xs></Grid>
        <Grid item xs="auto">
          {(successCheckout || isVaUser) && (
            <Button onClick={handleNext} disableElevation variant="contained">
              Next{" "}
              <Box
                sx={{ ml: 1 }}
                component="i"
                className="fa-solid fa-arrow-right"
              />
            </Button>
          )}
        </Grid>
      </Grid>

      <NotInterested
        open={searchParams.get("checkout") === "failed"}
        close={handleCloseNotInterestedDialog}
        setAlert={setAlert}
      />
    </>
  );
};

export default VAPaidPortal;
